import React, { useState } from 'react'
import debounce from 'lodash/debounce'
import omit from 'lodash/omit'

import Button from '@react/react-spectrum/Button'
import Select from '@react/react-spectrum/Select'
import { Table, THead, TBody, TH, TR, TD } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'

import Delete from '@react/react-spectrum/Icon/Delete'

// This debounced function needs to be outside of component.
// Otherwise debounce would get re-initiated with every re-render causing the function to behave like setTimeout instead of debounce.

const renameNewProp = debounce((newName, entries, setRows, setProp) => {
  if (newName && !Object.keys(entries).includes(newName)) {
    setProp(null)
    setRows({ ...entries, [newName]: '' })
  }
}, 1000)

export function KeyValueTable({ entries, allowedKeys, requiredKeys, disabled, onChange }) {
  const [newProp, setNewProp] = useState(null)

  let remainingKeys = [],
    filteredEntries = [],
    requiredEntries = []
  if (Array.isArray(entries)) {
    if (Array.isArray(allowedKeys)) {
      allowedKeys.forEach((key) => {
        let matchingEntry = entries.find((e) => e.key === key)
        if (matchingEntry) {
          filteredEntries.push(matchingEntry)
        } else {
          remainingKeys.push({ label: key, value: key })
        }
      })
    } else {
      filteredEntries = entries
    }
    if (Array.isArray(requiredKeys)) {
      requiredEntries = entries.filter((e) => requiredKeys.includes(e.key)) || []
    }
  }

  const rows = Array.isArray(filteredEntries)
    ? [...requiredEntries, ...filteredEntries].reduce((acc, it) => ({ ...acc, [it.key]: it.value }), {})
    : {}
  const _onChange = (r) => onChange(Object.entries(r).map(([key, value]) => ({ key, value })))

  const onPropRename = (oldName) =>
    debounce((newName) => {
      _onChange({ ...omit(rows, oldName), [newName]: rows[oldName] })
    }, 1000)

  const onNewPropRename = (newName) => {
    setNewProp((p) => ({ ...p, name: newName }))
    renameNewProp(newName, rows, _onChange, setNewProp)
  }

  const onPropRemove = (propName) => () => {
    _onChange(omit(rows, propName))
  }

  const canAddProperty = !disabled && !newProp && !(Array.isArray(allowedKeys) && remainingKeys.length === 0)
  const addProperty = () => {
    if (canAddProperty) {
      if (allowedKeys && remainingKeys.length > 0) {
        onNewPropRename(remainingKeys[0].value)
      } else {
        setNewProp({})
      }
    }
  }

  const getKeyOptions = (key) => [...remainingKeys, { label: key, value: key }]
  const getDisabled = (key) => disabled || (Array.isArray(requiredKeys) && requiredKeys.includes(key))

  return (
    <Table>
      <THead>
        <TH>property</TH>
        <TH>value</TH>
        <TH></TH>
      </THead>
      <TBody>
        {Object.keys(rows).map((key) => (
          <TR key={key}>
            <TD>
              {allowedKeys ? (
                <Select
                  options={getKeyOptions(key)}
                  value={key || ''}
                  onChange={onPropRename(key)}
                  disabled={getDisabled(key)}
                />
              ) : (
                <Textfield defaultValue={key || ''} onChange={onPropRename(key)} disabled={getDisabled(key)} />
              )}
            </TD>
            <TD>
              <Textfield
                value={rows[key] || ''}
                onChange={(v) => _onChange({ ...rows, [key]: v })}
                disabled={disabled}
              />
            </TD>
            <TD>
              <Button variant='tool' icon={<Delete />} onClick={onPropRemove(key)} disabled={getDisabled(key)} />
            </TD>
          </TR>
        ))}
        {newProp && (
          <TR>
            <TD>
              {allowedKeys ? (
                <Select options={remainingKeys} onChange={onNewPropRename} disabled={disabled} />
              ) : (
                <Textfield
                  value={newProp.name || ''}
                  onChange={onNewPropRename}
                  invalid={Object.keys(rows).includes(newProp.name)}
                  disabled={disabled}
                />
              )}
            </TD>
            <TD>
              <Textfield disabled />
            </TD>
            <TD>
              <Button variant='tool' icon={<Delete />} onClick={() => setNewProp(null)} disabled={disabled} />
            </TD>
          </TR>
        )}
        <TR onClick={addProperty}>
          <TD>
            <Button variant='action' quiet label='+ Add' disabled={!canAddProperty} />
          </TD>
          <TD />
          <TD />
        </TR>
      </TBody>
    </Table>
  )
}

KeyValueTable.defaultProps = {
  entries: [],
  fixedKeys: [],
  allowedKeys: [],
  requiredKeys: [],
  disabled: false,
  onChange: () => {},
}
